import { isAxiosError } from "axios";
import {
  qualityEventAuditSchema,
  QualityEventDefinition,
  qualityEventDocumentReferenceSchema,
  qualityEventRecordSchema,
} from "src/types/quality-events";
import { z } from "zod";
import { axiosClient } from "./backend";

export const getQualityEventDefinitions = async (orgId: string) =>
  (
    await axiosClient.get<QualityEventDefinition[]>(
      `organization/${orgId}/quality-events`
    )
  ).data;

export const getQualityEventDefinition = async (
  orgId: string,
  qualityEventKey: string
) =>
  (
    await axiosClient.get<QualityEventDefinition>(
      `organization/${orgId}/quality-events/${qualityEventKey}`
    )
  ).data;

export const getQualityEventRecords = async (
  orgId: string,
  qualityEventKey: string
) =>
  z.array(qualityEventRecordSchema).parse(
    (
      await axiosClient.get(`organization/${orgId}/quality-event-records`, {
        params: { qualityEventKey },
      })
    ).data
  );

export const getQualityEventRecord = async (orgId: string, recordId: string) =>
  qualityEventRecordSchema.parse(
    (
      await axiosClient.get(
        `organization/${orgId}/quality-event-records/${recordId}`
      )
    ).data
  );

export const getQualityEventAudit = async (orgId: string, recordId: string) =>
  qualityEventAuditSchema.parse(
    (
      await axiosClient.get(
        `organization/${orgId}/quality-event-records/${recordId}/audit-history`
      )
    ).data
  );

export const getQualityEventFiles = async (orgId: string) =>
  z
    .array(z.object({ id: z.string(), filename: z.string() }))
    .parse(
      (await axiosClient.get(`organization/${orgId}/quality-event-files`)).data
    );

export const getQualityEventDocumentReferences = async (orgId: string) =>
  z
    .array(qualityEventDocumentReferenceSchema)
    .parse(
      (
        await axiosClient.get(
          `organization/${orgId}/quality-event-document-references`
        )
      ).data
    );

export type QualityEventRecordUpdate = {
  expectedVersion: number;
  stepKey: string;
  title?: string;
  owner?: string | null;
  dueDate?: string | null;
  answers?: Record<string, unknown>;
  steps?: Record<
    string,
    {
      owner?: string | null;
      dueDate?: string | null;
      answers?: Record<string, unknown>;
    }
  >;
};

export const saveQualityEventRecord = async (
  orgId: string,
  request: { recordId: string; body: QualityEventRecordUpdate }
) =>
  qualityEventRecordSchema.parse(
    (
      await axiosClient.patch(
        `organization/${orgId}/quality-event-records/${request.recordId}`,
        request.body
      )
    ).data
  );

export const qualityEventErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    if (typeof error.response?.data === "string") return error.response.data;
    const data = z
      .object({ message: z.string(), missing: z.array(z.string()).optional() })
      .safeParse(error.response?.data);
    if (
      data.success &&
      data.data.message === "Complete the required answers before approving."
    )
      return "Approval failed. Required field values missing.";
    if (data.success)
      return [data.data.message, ...(data.data.missing ?? [])].join(" ");
  }
  return error instanceof Error && !(error instanceof z.ZodError)
    ? error.message
    : "Unable to save these changes. Please try again.";
};
